import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import merge from 'lodash-es/merge';

import { OPTIONS } from '@/constants';
import { SelectOption } from '@/models';
import { Customer } from '@/models/customer';
import { Batch, InventoryItem, ProductInventory, WMSInventoryItem } from '@/models/inventory';
import { TimePeriod } from '@/models/product';

/**
 * Returns an array of partnerIds based on an array of customerIds
 * @param customerIds - Customers assigned
 * @param customersKeyedById - Customers object
 * @returns array of partnerIds
 */
export const mapPartnerIds = (customerIds: Customer['id'][], customersKeyedById: { [key: string]: Customer }) => {
  return customerIds.map((id) => customersKeyedById[id].partnerId);
};

/**
 * Gets a period of time in days (i.e. "2 days")
 * @param isoDate - date in yyyy-MM-ddTHH:00:00-00:00 format
 * @returns a time period that can be measured in hours, days or weeks
 */
export const getTimePeriod = (isoDate: string) => {
  const now = new Date();

  const base: number = Date.parse(now.toISOString().substring(0, 19));

  const expiration: number = Date.parse(isoDate.substring(0, 19));

  const distance: string = formatDistanceStrict(expiration, base, {
    unit: OPTIONS.TIME_PERIODS.HOUR.value as 'hour',
    roundingMethod: 'round',
  });

  const values = distance.split(' ');

  const quantity = Number(values[0]);

  const timePeriod = {};

  if (quantity < 24) {
    // hours
    timePeriod['quantity'] = quantity;
    timePeriod['period'] = OPTIONS.TIME_PERIODS.HOUR.id;
  } else if (quantity < 168) {
    // days
    timePeriod['quantity'] = Math.round(quantity / 24);
    timePeriod['period'] = OPTIONS.TIME_PERIODS.DAY.id;
  } else {
    // weeks
    timePeriod['quantity'] = Math.round(quantity / 24 / 7);
    timePeriod['period'] = OPTIONS.TIME_PERIODS.WEEK.id;
  }

  return <TimePeriod>timePeriod;
};

/**
 *
 * @param quantity - quantity of hours, days, or weeks to offset from now
 * @param period - hour | day | week
 * @returns date with the format: yyyy-MM-dd HH:00:00
 */
export const convertTimePeriodToDate = (quantity: number, period: string) => {
  const now = new Date();

  switch (period) {
    case OPTIONS.TIME_PERIODS.HOUR.id:
      now.setHours(now.getHours() + quantity);
      break;
    case OPTIONS.TIME_PERIODS.DAY.id:
      now.setDate(now.getDate() + quantity);
      break;
    case OPTIONS.TIME_PERIODS.WEEK.id:
      now.setDate(now.getDate() + quantity * 7);
      break;
    default:
      now.setDate(now.getDate() + quantity);
  }

  const isoDateText =
    period === OPTIONS.TIME_PERIODS.HOUR.id
      ? `${now.toISOString().substring(0, 10)} ${now.toISOString().substring(11, 13)}:00:00`
      : `${now.toISOString().substring(0, 10)} 23:00:00`;

  return isoDateText;
};

/**
 * Filters for inventory items that are batch items
 * Adds the name property to each batch for the Stash's Select in manual orders
 * @param inventoryItems - array of inventory items, with null batches, or serialized batches
 * @returns array of batch inventory items sorted by date ascending and name
 */
export const formatBatchInventoryItemOptions = (inventoryItems: InventoryItem<Batch | null>[]) => {
  const batchInventoryItems = inventoryItems.reduce(
    (batchInventoryOptions: SelectOption<InventoryItem<Batch>>[], currentInventoryItem) => {
      const { batch } = currentInventoryItem;

      if (batch) {
        const option = {
          ...(currentInventoryItem as InventoryItem<Batch>),
          name: batch.productionBatchNumber,
        };

        batchInventoryOptions.push(option);
      }

      return batchInventoryOptions;
    },
    [],
  );

  const sortByBatchDate = (a: InventoryItem<Batch>, b: InventoryItem<Batch>): number => {
    const dateA = a?.batch?.batchDate;
    const dateB = b?.batch?.batchDate;

    if (!dateA) return !dateB ? 0 : 1;
    if (!dateB) return -1;

    return dateA.localeCompare(dateB);
  };

  const availableQuantity = (item: InventoryItem<Batch>): number => Number(item.quantity) - Number(item.reservedQty);

  const inventoryItemsInStock = batchInventoryItems.filter((item) => availableQuantity(item) > 0).sort(sortByBatchDate);
  const inventoryItemOutOfStock = batchInventoryItems
    .filter((item) => availableQuantity(item) === 0)
    .sort(sortByBatchDate);

  return [...inventoryItemsInStock, ...inventoryItemOutOfStock];
};

/**
 * Format params, adding double underscores for certain fields as expected by API
 * Removes the batch_associated_is_null param if the filter is `false`
 * @param payload - ServerListViewParams
 */
export const formatProductsParams = ({
  excludeList = [],
  serialized,
}: {
  excludeList: string[];
  serialized: string;
}): Record<string, unknown[]> => {
  const searchParams = new URLSearchParams(serialized);
  const formattedParams: Record<string, unknown[]> = { error_format: ['jsonapi'] };

  for (const [key, value] of searchParams.entries()) {
    if (excludeList.includes(key)) {
      continue;
    }

    let formattedKey = key;

    if (formattedKey === 'license_type') {
      formattedKey = 'license__type';
    }

    if (formattedKey.includes('_is_null')) {
      formattedKey = formattedKey.replace(/_is_null/g, '__isnull');
    }

    if (!Object.hasOwn(formattedParams, formattedKey)) {
      formattedParams[formattedKey] = [value];
      continue;
    }

    formattedParams[formattedKey].push(value);
  }

  formattedParams.limit = formattedParams.limit || '9999';

  if (formattedParams.batch_associated__isnull?.[0] === 'false') {
    delete formattedParams.batch_associated__isnull;
  }

  return formattedParams;
};

export const formatFacilities = (data) =>
  data.map((facility) => ({
    ...facility,
    lat: Number(facility.lat),
    long: Number(facility.long),
  }));

/**
 * Aggregate quantities of a product's inventory items
 * @returns sum of inventory items quanitity or reserved quantity
 */
const aggregateQuantities = (
  inventoryItems: InventoryItem<Batch | null>[],
  key: InventoryItem['quantityInProductUom' | 'reservedQtyInProductUom'],
) => {
  return inventoryItems.reduce((sum, currentItem) => {
    if (currentItem.isArchived) {
      return sum;
    }

    return (sum += +currentItem[key]);
  }, 0);
};

/**
 * FIXME: [DANK-180] This logic should reside on the API, with the product-level values already reflecting
 * FIXME: [DANK-180] inventory item quantities. Update the API and remove this function when possible.
 *
 * Update a product's availableInventory, quantity and reservedQty properties to be sum from inventory items
 * Inventory items are an array of objects with quantity and reservedQty properties
 *
 * Note: We use the Unit Of Measure (UOM) fields to ensure calculations are in the current product's unit, not their
 * parent product in the case of inherited subproducts.
 *
 * @returns products with quantites and reservedQtys determined by inv items
 */
export const updateWithInvItemQuantities = (products: ProductInventory[]) =>
  products.map((product) => {
    const { inventoryItems } = product;

    const quantity = aggregateQuantities(inventoryItems, 'quantityInProductUom');
    const reservedQty = aggregateQuantities(inventoryItems, 'reservedQtyInProductUom');
    const availableInventory = quantity - reservedQty;

    return merge({}, product, {
      quantity,
      reservedQty,
      availableInventory,
    });
  });

export const parseProductInventory = (results: ProductInventory[], wmsProductInventory: WMSInventoryItem[] = []) => {
  return results.map((product) => {
    // TODO: iterate this to do filter on backend
    product.inventoryItems = product.inventoryItems.filter((item) => !item.isArchived);

    // TODO: Let's move wms lot items as inventory items to the backend
    // Determine if the product has WMS lot inventory items
    const wmsInventoryItem = wmsProductInventory.find(
      (wmsProductInventoryItem) => wmsProductInventoryItem.product.id === product.id,
    );

    // If the product has WMS lot inventory items, replace the WMS facility inventory item with the corresponding read-only WMS lot inventory items
    if (wmsInventoryItem) {
      product.inventoryItems = product.inventoryItems.reduce<InventoryItem<Batch | null>[]>(
        (inventoryItems, inventoryItem) => {
          // The default facility inventory item
          let inventoryItemsToRender = [inventoryItem];

          // Map lot inventory items to default inventory item structure
          if (typeof inventoryItem.warehouse === 'object' && inventoryItem.warehouse.isLeaflinkFacility) {
            inventoryItemsToRender = wmsInventoryItem.lotInventory.map<InventoryItem<Batch | null>>(
              (lotInventoryItem) => ({
                ...inventoryItem,
                id: Number(`${inventoryItem.id}${lotInventoryItem.lotId}`),
                quantity: String(lotInventoryItem.quantity),
                reservedQty: String(wmsInventoryItem.openOrderQuantity),
                wmsLotInventoryItem: lotInventoryItem,
              }),
            );
          }

          inventoryItems.push(...inventoryItemsToRender);

          return inventoryItems;
        },
        [],
      );
    }

    // Aggregates quantities from line items
    const { quantity, reservedQty } = product.inventoryItems.reduce(
      (prev, current) => {
        return {
          quantity: prev.quantity + parseFloat(current.quantity),
          reservedQty: prev.reservedQty + parseFloat(current.reservedQty),
        };
      },
      {
        quantity: 0,
        reservedQty: 0,
      },
    );

    product.quantity = quantity.toString();
    product.reservedQty = reservedQty.toString();

    return product;
  });
};
